import {
  defaultInterpolationFormat,
  I18NEXT_NAMESPACE,
  I18NEXT_SERVICE,
  I18NextLoadResult,
  I18NextModule,
  ITranslationService,
} from 'angular-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { InitOptions } from 'i18next';
import {APP_INITIALIZER, LOCALE_ID, Provider} from '@angular/core';

export enum LanguageLocale {
  AM = 'am',
  AR = 'ar',
  BN = 'bn',
  DE = 'de',
  EN = 'en',
  ES = 'es',
  FR = 'fr',
  HI = 'hi',
  ID = 'id',
  IT = 'it',
  JA = 'ja',
  KK = 'kk',
  KO = 'ko',
  MG = 'mg',
  MS = 'ms',
  PL = 'pl',
  PRS = 'prs',
  PS = 'ps',
  PT = 'pt',
  RU = 'ru',
  SW = 'sw',
  TH = 'th',
  TL = 'tl',
  FIL = 'fil',
  TR = 'tr',
  UR = 'ur',
  UZ = 'uz',
  VI = 'vi',
  ZH_CN = 'zh',
}
export const DEFAULT_LANGUAGE = LanguageLocale.EN;

/** Маппинг локали на имя файла */
const LOCALE_FILE_MAP: Record<string, string> = {
  [LanguageLocale.AM]: 'am',
  [LanguageLocale.AR]: 'ar',
  [LanguageLocale.BN]: 'bn',
  [LanguageLocale.DE]: 'de',
  [LanguageLocale.EN]: 'en',
  [LanguageLocale.ES]: 'es',
  [LanguageLocale.FR]: 'fr',
  [LanguageLocale.HI]: 'hi',
  [LanguageLocale.ID]: 'id',
  [LanguageLocale.IT]: 'it',
  [LanguageLocale.JA]: 'ja',
  [LanguageLocale.KK]: 'kk',
  [LanguageLocale.KO]: 'ko',
  [LanguageLocale.MG]: 'mg',
  [LanguageLocale.MS]: 'ms',
  [LanguageLocale.PL]: 'pl',
  [LanguageLocale.PRS]: 'prs',
  [LanguageLocale.PS]: 'ps_AF',
  [LanguageLocale.PT]: 'pt',
  [LanguageLocale.RU]: 'ru',
  [LanguageLocale.SW]: 'sw',
  [LanguageLocale.TH]: 'th',
  [LanguageLocale.TL]: 'tl',
  [LanguageLocale.FIL]: 'tl',
  [LanguageLocale.TR]: 'tr',
  [LanguageLocale.UR]: 'ur',
  [LanguageLocale.UZ]: 'uz',
  [LanguageLocale.VI]: 'vi',
  [LanguageLocale.ZH_CN]: 'zh_CN',
};

async function loadTranslations(): Promise<InitOptions['resources']> {
  const entries = Object.entries(LOCALE_FILE_MAP);

  const modules = await Promise.all(
    entries.map(([, file]) =>
      import(`../../../assets/locales/c/${file}.json`)
    )
  );

  const resources: Record<string, { translations: Record<string, string> }> = {};
  entries.forEach(([locale], index) => {
    resources[locale] = { translations: modules[index].default };
  });

  return resources;
}

function buildI18nextOptions(resources: InitOptions['resources']): InitOptions {
  return {
    resources,
    fallbackLng: {
      'en-US': [LanguageLocale.EN],
      'ru-RU': [LanguageLocale.RU],
      default: [DEFAULT_LANGUAGE],
    },
    supportedLngs: Object.values(LanguageLocale),
    returnEmptyString: false,

    // have a common namespace used around the full app
    defaultNS: 'translations',
    load: 'currentOnly',
    keySeparator: '.', // we use content as keys
    nsSeparator: ':',
    interpolation: {
      format: I18NextModule.interpolationFormat(defaultInterpolationFormat),
    },

    // lang detection plugin options
    detection: {
      // order and from where users language should be detected
      order: ['path'],
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
      caches: ['localStorage'],
      excludeCacheFor: ['cimode'], // languages to not persist (cookie, localStorage)
      cookieMinutes: 0,
      cookieDomain: '/',
    },
  };
}

//  Инициализация i18next
export function appInit(i18next: ITranslationService): () => Promise<I18NextLoadResult | void> {
  return async () => {
    const resources = await loadTranslations();
    return i18next
      .use(LanguageDetector)
      .init(buildI18nextOptions(resources));
  };
}

export function localeIdFactory(i18next: ITranslationService): string {
  return i18next.language;
}

export const I18N_PROVIDERS: Provider[] = [
  {
    provide: APP_INITIALIZER,
    useFactory: appInit,
    deps: [ I18NEXT_SERVICE ],
    multi: true,
  },
  {
    provide: I18NEXT_NAMESPACE,
    useValue: ['translations', 'validationErrors'],
  },
  {
    provide: LOCALE_ID,
    deps: [ I18NEXT_SERVICE ],
    useFactory: localeIdFactory,
  },
];
